// src/pages/Leaderboard.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../utils/apiClient';

// Custom Sheriff Star Component for Texas
const SheriffStar = ({ size = "w-4 h-4" }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L15.09 8.26L22 9L17 14.14L18.18 21L12 17.77L5.82 21L7 14.14L2 9L8.91 8.26L12 2Z"
      fill="#FFD700"
    />
  </svg>
);

// Comment Message Component - SINGLE DEFINITION
const CommentMessage = ({ message, currentUser, onEdit, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.message);
  const [isHovered, setIsHovered] = useState(false);

  const isOwnMessage = currentUser && message.user && message.user._id === currentUser.id;
  const canEdit = isOwnMessage && new Date() - new Date(message.timestamp) < 5 * 60 * 1000; // 5 minutes

  const handleEdit = async () => {
    if (editText.trim() !== message.message) {
      await onEdit(message._id, editText.trim());
    }
    setIsEditing(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div 
      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-3">
          {/* Avatar placeholder */}
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
            {message.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <span className="font-semibold text-gray-900">{message.username}</span>
            <span className="text-sm text-gray-500 ml-2">
              {formatTime(message.timestamp)}
              {message.edited && <span className="ml-1 italic">(edited)</span>}
            </span>
          </div>
        </div>
        
        {isHovered && canEdit && (
          <div className="flex space-x-2">
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(message._id)}
              className="text-sm text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        )}
      </div>
      
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white resize-none"
            rows="3"
            maxLength={500}
            autoFocus
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{editText.length}/500 characters</span>
            <div className="flex space-x-2">
              <button
                onClick={handleEdit}
                className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
              >
                Save
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-800 whitespace-pre-wrap break-words leading-relaxed">{message.message}</p>
      )}
    </div>
  );
};

const Comments = ({ currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

const fetchMessages = useCallback(async () => {
  try {
    setLoading(true);
    setError('');
    
    const response = await apiClient.get('/api/chat/messages?page=1&limit=50');
    
    // Reverse the messages array to show newest first
    const sortedMessages = (response.data.messages || []).reverse();
    setMessages(sortedMessages);
    setSuccess(`✅ Loaded ${response.data.messages?.length || 0} comments`);
  } catch (error) {
    console.error('Error fetching messages:', error);
    if (error.response?.status === 401) {
      setError('Please log in to view comments');
    } else {
      setError('Failed to load comments. Please try again.');
    }
  } finally {
    setLoading(false);
  }
}, []);

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      setError('');
      
      const response = await apiClient.post('/api/chat/messages', {
        message: newMessage.trim()
      });

      setMessages(prev => [response.data, ...prev]);
      setNewMessage('');
      setSuccess('✅ Comment posted successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.response?.status === 401) {
        setError('Please log in to post comments');
      } else if (error.response?.status === 429) {
        setError('Too many messages. Please wait before posting again.');
      } else {
        const errorMessage = error.response?.data?.message || 'Failed to send comment';
        setError(errorMessage);
      }
    } finally {
      setSending(false);
    }
  };

  const editMessage = async (messageId, newText) => {
    try {
      setError('');
      const response = await apiClient.put(`/api/chat/messages/${messageId}`, {
        message: newText
      });

      setMessages(prev => prev.map(msg => 
        msg._id === messageId ? response.data : msg
      ));
      setSuccess('✅ Comment updated!');
    } catch (error) {
      console.error('Error editing message:', error);
      setError(error.response?.data?.message || 'Failed to edit comment');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      setError('');
      await apiClient.delete(`/api/chat/messages/${messageId}`);
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      setSuccess('✅ Comment deleted!');
    } catch (error) {
      console.error('Error deleting message:', error);
      setError(error.response?.data?.message || 'Failed to delete comment');
    }
  };

  // Auto-load messages on mount
  useEffect(() => {
    fetchMessages();
    
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Clear success messages after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div id="comments-section" className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Smack talk</h2>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">{messages.length} comments</span>
          <button 
            onClick={fetchMessages}
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {success}
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Comment Form */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
            {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Share your thoughts about the leaderboard, predictions, or F1 in general..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white resize-none"
              rows="3"
              maxLength={500}
              disabled={sending}
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-gray-500">{newMessage.length}/500 characters</span>
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {sending ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Comments List */}
      <div className="space-y-4">
        {loading && messages.length === 0 && (
          <div className="text-center text-gray-500 py-8">Loading comments...</div>
        )}
        
        {messages.length === 0 && !loading && (
          <div className="text-center text-gray-500 py-8">
            <div className="text-4xl mb-2">💬</div>
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        )}

        {messages.map((message) => (
          <CommentMessage
            key={message._id}
            message={message}
            currentUser={currentUser}
            onEdit={editMessage}
            onDelete={deleteMessage}
          />
        ))}
      </div>
    </div>
  );
};

function Leaderboard() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [races, setRaces] = useState([]);
  const [raceScores, setRaceScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Define marquee races with their badge info
  const marqueeRaces = {
    'monaco': {
      name: 'Monaco GP',
      badge: '🎲',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      title: 'Monaco Dice',
      isCustom: false
    },
    'silverstone': {
      name: 'British GP', 
      badge: '⚔️',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      title: 'Silverstone Sword',
      isCustom: false
    },
    'spa': {
      name: 'Spa GP',
      badge: '🌲', 
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      title: 'Spa Forest',
      isCustom: false
    },
    'monza': {
      name: 'Monza GP',
      badge: '🏛️',
      color: 'text-green-600', 
      bgColor: 'bg-green-100',
      title: 'Monza Temple of Speed',
      isCustom: false
    },
    'cota': {
      name: 'COTA GP',
      badge: <SheriffStar size="w-4 h-4" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-800',
      title: 'Texas Sheriff',
      isCustom: true
    },
    'abu-dhabi': {
      name: 'Abu Dhabi GP',
      badge: '🐪',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100', 
      title: 'Abu Dhabi Desert Camel',
      isCustom: false
    }
  };

  // Helper function to check if predictions are open for a race (FIXED VERSION)
  const isPredictionsOpen = useCallback((race) => {
    if (!race) return false;
    
    const now = new Date();
    const raceDate = new Date(race.date);
    
    // If race date is in the past, predictions are closed
    if (raceDate < now) return false;
    
    // If qualifying has already happened, predictions are closed
    if (race.qualifyingDateTime) {
      const qualifyingTime = new Date(race.qualifyingDateTime);
      // FIX: Check prediction closing time (2 minutes before qualifying)
      const predictionClosingTime = new Date(qualifyingTime.getTime() - (2 * 60 * 1000));
      if (predictionClosingTime < now) return false;
    }
    
    return true;
  }, []);

  // Helper function to check if tips are closed (predictions were open but now closed)
  const areTipsClosed = useCallback((race) => {
    if (!race) return false;
    
    // Tips are closed if predictions are not open
    // This means either the race has happened or qualifying has started
    return !isPredictionsOpen(race);
  }, [isPredictionsOpen]);

  // Check if a race is a marquee race
  const getMarqueeRaceInfo = (raceName) => {
    const normalizedName = raceName.toLowerCase();
    
    console.log(`Checking race: "${raceName}" -> normalized: "${normalizedName}"`);
    
    if (normalizedName.includes('monaco')) {
      console.log('Matched Monaco');
      return marqueeRaces.monaco;
    }
    if (normalizedName.includes('british') || normalizedName.includes('silverstone')) {
      console.log('Matched British/Silverstone');
      return marqueeRaces.silverstone;
    }
    if (normalizedName.includes('belgian') || normalizedName.includes('belgium')) {
      console.log('Matched Belgian/Belgium');
      return marqueeRaces.spa;
    }
    if (normalizedName.includes('monza') || normalizedName.includes('italian')) {
      console.log('Matched Monza/Italian');
      return marqueeRaces.monza;
    }
    if (normalizedName.includes('austin') || normalizedName.includes('cota') || normalizedName.includes('united states')) {
      console.log('Matched Austin/COTA/United States');
      return marqueeRaces.cota;
    }
    if (normalizedName.includes('abu dhabi')) {
      console.log('Matched Abu Dhabi');
      return marqueeRaces['abu-dhabi'];
    }
    
    console.log('No match found');
    return null;
  };

  // Get user's badges based on race wins (SIMPLE VERSION - only for completed races)
  const getUserBadges = (userId) => {
    const badges = [];
    
    races.forEach(race => {
      const marqueeInfo = getMarqueeRaceInfo(race.raceName);
      
      // Debug: Log all marquee races and their status
      if (marqueeInfo) {
        console.log(`MARQUEE RACE: ${race.raceName}, Status: ${race.status}, Date: ${race.date}`);
      }
      
      // Only award badges for races that have status 'completed'
      if (marqueeInfo && race.status === 'completed') {
        console.log(`PROCESSING COMPLETED MARQUEE RACE: ${race.raceName}`);
        
        // Check if this user won this race
        if (raceScores[race._id]) {
          const raceResults = Object.entries(raceScores[race._id])
            .map(([uid, score]) => ({ userId: uid, score }))
            .sort((a, b) => b.score - a.score);
          
          // Check if this user won (highest score) and has score > 0
          if (raceResults.length > 0 && 
              raceResults[0].userId === userId && 
              raceResults[0].score > 0) {
            console.log(`AWARDING BADGE to user ${userId} for race ${race.raceName}`);
            badges.push(marqueeInfo);
          }
        }
      }
    });
    
    console.log(`User ${userId} total badges:`, badges.length);
    return badges;
  };

  // Get current user info
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Get username from token or make an API call to get user details
        setCurrentUser({ 
          id: payload.userId,
          username: payload.username || 'User' // Fallback if username not in token
        });
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }, []);

  // Handle scrolling to comments section based on URL hash
  useEffect(() => {
    const scrollToComments = () => {
      // Check both location.hash and window.location.hash
      const hasCommentsHash = location.hash === '#comments' || window.location.hash === '#comments';
      
      console.log('Scroll effect running. Hash check:', {
        locationHash: location.hash,
        windowHash: window.location.hash,
        hasCommentsHash,
        loading,
        usersLength: users.length,
        racesLength: races.length
      });
      
      if (hasCommentsHash) {
        console.log('Attempting to scroll to comments section...');
        
        const attemptScroll = (attempts = 0) => {
          const commentsSection = document.getElementById('comments-section');
          console.log(`Attempt ${attempts + 1}: Comments section found:`, !!commentsSection);
          
          if (commentsSection) {
            console.log('Scrolling to comments section');
            // Force a slight delay even after finding the element
            setTimeout(() => {
              commentsSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start' 
              });
              // Also try scrolling to a specific pixel position as backup
              setTimeout(() => {
                const rect = commentsSection.getBoundingClientRect();
                const scrollTop = window.pageYOffset + rect.top - 100; // 100px offset from top
                window.scrollTo({ top: scrollTop, behavior: 'smooth' });
                console.log('Backup scroll executed to position:', scrollTop);
              }, 100);
            }, 200);
          } else if (attempts < 20) {
            // Increased attempts to 20 (10 seconds total)
            setTimeout(() => attemptScroll(attempts + 1), 500);
          } else {
            console.log('Failed to find comments section after 20 attempts');
          }
        };

        // Start attempting immediately after the effect runs
        attemptScroll();
      }
    };

    // Always try to scroll when the effect runs, regardless of loading state
    scrollToComments();
  }, [location.hash, loading, users.length, races.length]); // Watch more dependencies
  
  // Also add a separate effect that runs when the page first loads with a hash
  useEffect(() => {
    if (window.location.hash === '#comments') {
      console.log('Initial page load with comments hash detected');
      // Set a longer timeout for initial page loads
      setTimeout(() => {
        const commentsSection = document.getElementById('comments-section');
        if (commentsSection) {
          console.log('Initial scroll to comments section');
          commentsSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start' 
          });
        }
      }, 2000); // 2 second delay for initial page loads
    }
  }, []); // Run only once on component mount

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 1. Fetch leaderboard data (users with total points)
        const leaderboardResponse = await apiClient.get('/api/scores/leaderboard');
        
        // 2. Fetch all races
        const racesResponse = await apiClient.get('/api/f1data/races/all');
        
        // Ensure race data is valid
        if (!Array.isArray(racesResponse.data)) {
          console.error('Race data is not an array:', racesResponse.data);
          setError('Invalid race data format');
          setLoading(false);
          return;
        }
        
        console.log(`Found ${racesResponse.data.length} total races`);
        
        // 3. Filter completed races
        const now = new Date();
        const completedRaces = racesResponse.data.filter(race => 
          race && (
            race.status === 'completed' || 
            (race.date && new Date(race.date) < now)
          )
        );
        
        console.log(`Found ${completedRaces.length} completed races`);
        
        // 4. Sort races by date (most recent first)
        completedRaces.sort((a, b) => {
          if (!a.date) return 1;
          if (!b.date) return -1;
          return new Date(b.date) - new Date(a.date);
        });
        
        // 5. Store users and races
        const userList = Array.isArray(leaderboardResponse.data) ? leaderboardResponse.data : [];
        setUsers(userList);
        setRaces(completedRaces);
        
        // 6. Fetch user predictions for each race to get points
        const scores = {};
        
        // For each race, try to get predictions
        for (const race of completedRaces) {
          try {
            // First create an empty scores object for this race
            scores[race._id] = {};
            
            // Try to get scores from the scores endpoint
            const raceScoreResponse = await apiClient.get(`/api/scores/race/${race._id}`);
            
            if (raceScoreResponse.data && raceScoreResponse.data.scores) {
              // Map scores by user ID for quick lookup
              raceScoreResponse.data.scores.forEach(score => {
                if (score.user && score.score > 0) { // Only include users with scores > 0
                  scores[race._id][score.user] = score.score;
                }
              });
            }
          } catch (scoreError) {
            console.log(`Could not fetch scores for race ${race._id}:`, scoreError);
            // Continue without scores for this race
          }
        }
        
        console.log('Collected race scores:', scores);
        setRaceScores(scores);
        setError(null);
      } catch (err) {
        setError('Failed to load data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to calculate user statistics
  const calculateUserStats = (userId) => {
    const userScores = [];
    let totalPoints = 0;
    let racesParticipated = 0;

    // Go through all races and collect this user's scores
    races.forEach(race => {
      const score = getUserRaceScore(userId, race._id);
      if (score > 0) {
        userScores.push(score);
        totalPoints += score;
        racesParticipated++;
      }
    });

    const averageScore = racesParticipated > 0 ? totalPoints / racesParticipated : 0;

    return {
      totalPoints,
      racesParticipated,
      averageScore: Math.round(averageScore * 100) / 100, // Round to 2 decimal places
      userScores
    };
  };

  // Helper function to get a user's score for a specific race
  const getUserRaceScore = (userId, raceId) => {
    // Check if we have scores for this race
    if (raceScores[raceId] && raceScores[raceId][userId] !== undefined) {
      return raceScores[raceId][userId];
    }
    
    return 0; // Default score if no data found
  };

  // Create enhanced user data with calculated stats and sort by average score
  const enhancedUsers = users.map(user => ({
    ...user,
    stats: calculateUserStats(user.id),
    badges: getUserBadges(user.id)
  })).sort((a, b) => {
    // Primary sort: by average score (descending)
    if (b.stats.averageScore !== a.stats.averageScore) {
      return b.stats.averageScore - a.stats.averageScore;
    }
    // Secondary sort: by races participated (descending) - more races = tie-breaker
    if (b.stats.racesParticipated !== a.stats.racesParticipated) {
      return b.stats.racesParticipated - a.stats.racesParticipated;
    }
    // Tertiary sort: by total points (descending)
    return b.stats.totalPoints - a.stats.totalPoints;
  });

  if (loading) {
    return <div className="text-center py-10">Loading leaderboard...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Leaderboard</h1>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden overflow-x-auto mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[60px]">
                Rank
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[60px] bg-gray-50 z-10 min-w-[200px]">
                Username
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[260px] bg-gray-50 z-10 min-w-[80px]">
                Avg Score
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[340px] bg-gray-50 z-10 min-w-[80px]">
                Total Pts
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[420px] bg-gray-50 z-10 min-w-[70px]">
                Races
              </th>
              
              {/* Dynamic race columns */}
              {races.map(race => (
                <th key={race._id} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[60px]">
                  {race.circuit?.country || (race.raceName ? race.raceName.split(' ')[0] : 'Race')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {enhancedUsers.map((user, index) => (
              <tr key={user.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-inherit z-10 font-medium text-center">
                  {index + 1}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-[60px] bg-inherit z-10">
                  <div className="flex items-center space-x-2">
                    <span>{user.username}</span>
                    {/* Display badges */}
                    <div className="flex space-x-1">
                      {user.badges.map((badge, badgeIndex) => (
                        <div
                          key={badgeIndex}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}
                          title={`${badge.title} - ${badge.name} Winner`}
                        >
                          {badge.isCustom ? (
                            <span className="flex items-center">{badge.badge}</span>
                          ) : (
                            <span className="text-sm">{badge.badge}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-blue-600 sticky left-[260px] bg-inherit z-10 text-center">
                  {user.stats.averageScore.toFixed(1)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 sticky left-[340px] bg-inherit z-10 text-center">
                  {user.stats.totalPoints}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 sticky left-[420px] bg-inherit z-10 text-center">
                  {user.stats.racesParticipated}
                </td>
                
                {/* Display race scores */}
                {races.map(race => {
                  const points = getUserRaceScore(user.id, race._id);
                  
                  return (
                    <td key={`${user.id}-${race._id}`} className="px-3 py-4 whitespace-nowrap text-sm text-center">
                      <span className={`font-medium ${points > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {points > 0 ? points : '-'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend/Info section */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Marquee Race Winner Badge Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-blue-700">
          {Object.entries(marqueeRaces).map(([key, badge]) => (
            <div key={key} className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}>
                {badge.isCustom ? (
                  <span className="flex items-center">{badge.badge}</span>
                ) : (
                  <span className="text-sm">{badge.badge}</span>
                )}
              </span>
              <span>{badge.title}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs text-blue-600">
          <p><strong>Ranking System:</strong> Average score → Races participated → Total points</p>
          <p>Badges are awarded to winners of marquee races (Monaco, Silverstone, Spa, Monza, COTA, Abu Dhabi)</p>
        </div>
      </div>

      {/* Comments Section */}
      <Comments currentUser={currentUser} />
    </div>
  );
}

export default Leaderboard;